import "./redeem-reward.scss";

import { MessageResponseDto } from "@api/api";
import { Constraint } from "@core/model/constraint.enum";
import { EarnPreference } from "@core/model/earn-preference.enum";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import GridItem, { GridItemModel } from "@core/ui/grid-item/grid-item";
import { GetRewardCampaignModel } from "@factory/model/query-model-reward";
import { useQyRedeemReward } from "@factory/query/reward.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import Scan from "@shared/ui/scan/scan";
import { Button } from "primereact/button";
import { DataView } from "primereact/dataview";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { RadioButton } from "primereact/radiobutton";
import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import { useSphereRedeemItemCtx } from "../sphere-redeem-item/sphere-redeem-item.context";

export interface RedeemRewardProps {}

export function RedeemReward() {
  // DECLARATIONS
  const [rowLimit, setRowLimit] = useState(20);
  const [, setPageNumber] = useState(0);
  const [first, setFirst] = useState(0);
  const {
    campaigns,
    branchOptions,
    selectedBranch,
    selectedRedeemCampaign,
    cardAlias,
    setSelectedRedeemCampaign,
    setSelectedBranch,
    getCampaign,
    setCardAlias,
  } = useSphereRedeemItemCtx();
  const { getUserRole } = useUserIdentityContext();
  const { tenant } = getUserRole();
  const { showSuccess } = useNotificationContext();
  const navigate = useNavigate();
  const [dialogVisible, setDialogVisible] = useState(false);
  const { mapRedeemReward } = useFormApiMapper();
  const [scan, setScan] = useState(false);
  const [preference, setPreference] = useState(EarnPreference.POINTS);

  // API
  const handleRedeemSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;
    setDialogVisible(false);

    showSuccess(`Redemption complete`);
    navigate(`success?points=${data.points}&stamp=${data.stamp}&return=redeem`);
  };
  const { mutate: redeem } = useQyRedeemReward(handleRedeemSuccess);

  // LOCAL FUNCTION
  const handleSuccessScan = (value: string) => {
    setCardAlias(value);
    setTimeout(() => {
      getCampaign({ code: value, tenant });
    }, 300);
  };
  const handleCloseScan = () => {
    setScan(false);
  };
  const handleSelectRedeem = (campaign: GridItemModel) => {
    setSelectedRedeemCampaign(campaign);
    setDialogVisible(true);
  };
  const handleRedeem = () => {
    if (!cardAlias || !selectedRedeemCampaign?.code) {
      return;
    }

    const value = mapRedeemReward(
      cardAlias,
      selectedRedeemCampaign.code,
      selectedBranch,
      tenant,
      preference,
      undefined,
      undefined
    );
    redeem(value);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") {
      return;
    }

    getCampaign({ code: cardAlias, tenant });
  };

  // LOCAL RENDERS
  const footerContent = (
    <div>
      <Button
        className="block w-full mt-2"
        label="Redeem"
        onClick={() => handleRedeem()}
      />
      <Button
        className="block w-full mt-2"
        label="Close"
        severity="secondary"
        outlined
        onClick={() => setDialogVisible(false)}
      />
    </div>
  );
  const radioGroup = (
    <section>
      <p>Select Redemption Preference</p>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex align-items-center">
          <RadioButton
            inputId="preference-point"
            name="preference"
            value={EarnPreference.POINTS}
            onChange={(e) => setPreference(e.value)}
            checked={preference === EarnPreference.POINTS}
          />
          <label htmlFor="preference-point" className="ml-2">
            {EarnPreference.POINTS}
          </label>
        </div>

        <div className="flex align-items-center">
          <RadioButton
            inputId="preference-stamp"
            name="preference"
            value={EarnPreference.STAMP}
            onChange={(e) => setPreference(e.value)}
            checked={preference === EarnPreference.STAMP}
          />
          <label htmlFor="preference-stamp" className="ml-2">
            {EarnPreference.STAMP}
          </label>
        </div>
      </div>
    </section>
  );
  const gridItem = (campaign: GetRewardCampaignModel) => {
    const model = {
      name: campaign.name,
      image: campaign.image,
      description: campaign.description,
      points: campaign.points,
      stamp: campaign.stamp,
      expirationDate: campaign.end_date,
      code: campaign.code,
      constraint: campaign.constraint,
    } as GridItemModel;
    return (
      <GridItem
        campaign={model}
        key={campaign.code}
        onRedeem={handleSelectRedeem}
      />
    );
  };
  const listTemplate = (campaigns: GetRewardCampaignModel[]): any => {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 mt-4`}>
        {campaigns.map((campaign) => gridItem(campaign))}
      </div>
    );
  };
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{campaigns?.length}</b>
      </h5>

      <DataView
        value={campaigns}
        listTemplate={listTemplate}
        paginator
        rows={rowLimit}
        first={first}
        totalRecords={campaigns?.length}
        rowsPerPageOptions={[10, 20, 30]}
        onPage={onPageChange}
      />
    </section>
  );

  return (
    <div id="RedeemReward" className="redeem-reward">
      <HeaderContent title={<h1>Redeem Rewards</h1>}>
        <div className="p-inputgroup flex-1">
          <FloatLabel>
            <InputText
              id="userCode"
              value={cardAlias}
              onChange={(e) => setCardAlias(e.target.value)}
              keyfilter="int"
              className="text-4xl w-full"
              autoFocus
              tabIndex={0}
              onKeyDown={handleKeyDown}
            />
            <label htmlFor="amount">Enter User Code</label>
          </FloatLabel>

          <span
            className="p-inputgroup-addon cursor-pointer px-8 bg-emerald-500 border border-emerald-400"
            onClick={() => setScan(true)}
          >
            <i className="pi pi-camera text-white"></i>
          </span>
        </div>
      </HeaderContent>

      <Dialog
        header={
          selectedRedeemCampaign?.name
            ? `Redeem ${selectedRedeemCampaign?.name}`
            : "Redeem"
        }
        visible={dialogVisible}
        style={{ width: "50vw" }}
        onHide={() => {
          if (!dialogVisible) return;
          setDialogVisible(false);
        }}
        footer={footerContent}
      >
        <div className="m-0">
          {selectedRedeemCampaign?.constraint === Constraint.OR
            ? radioGroup
            : null}

          {selectedRedeemCampaign ? (
            <div>
              <GridItem campaign={selectedRedeemCampaign} />
              <p>{selectedRedeemCampaign.description}</p>
            </div>
          ) : null}
        </div>
      </Dialog>

      <div className="p-7">
        {scan ? (
          <Scan
            successScan={handleSuccessScan}
            closeScan={handleCloseScan}
            isCloseOnScan={true}
          />
        ) : null}

        <Dropdown
          value={selectedBranch}
          onChange={(e) => setSelectedBranch(e.value)}
          options={branchOptions}
          placeholder="Select a branch"
          className="w-full md:w-14rem text-4xl"
        />

        {campaigns ? grid : null}
        <Outlet />
      </div>
    </div>
  );
}

export default RedeemReward;
